<template>
  <div class="map-info-bar" role="status" aria-live="off">
    <span class="material-icons map-info-bar__icon" :title="focusSourceTitle">{{ focusSourceIcon }}</span>
    <template v-for="(slot, i) in visibleSlots" :key="i">
      <span class="map-info-bar__slot" :title="slot.label">{{ slot.value }}</span>
    </template>
    <button
      v-if="settings.showMiniCompass"
      class="map-info-bar__compass"
      type="button"
      :aria-label="$t('map.info_bar.reset_map_to_north')"
      :title="$t('map.info_bar.reset_map_to_north_2')"
      @click="resetNorth"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" :style="{ transform: `rotate(${compassRotationDeg}deg)` }" aria-hidden="true">
        <path d="M8 1 L10.6 8 L8 6.6 L5.4 8 Z" fill="#fddf00"/>
        <path d="M8 15 L5.4 8 L8 9.4 L10.6 8 Z" fill="#ffffff" opacity="0.5"/>
      </svg>
    </button>
    <button
      class="map-info-bar__settings"
      type="button"
      :aria-label="$t('map.info_bar.map_information_bar_settings')"
      :title="$t('map.info_bar.map_information_bar_settings_2')"
      @click="$emit('open-settings')"
    >
      <span class="material-icons">tune</span>
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { mapState } from 'vuex';
import {
  compassRotation,
  formatSlotValue,
  MapInfoBarSettings,
  slotLabel,
} from '../../../utils/mapInfoBarSettings';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';

/**
 * The bar itself is deliberately dumb: every value comes from the `map` Vuex module (focus point,
 * formatted coordinates, elevation, user fix) and every formatting decision lives in
 * utils/mapInfoBarSettings.ts so it can be unit tested.
 */
export default defineComponent({
  name: 'MapInfoBar',
  props: {
    settings: { type: Object as PropType<MapInfoBarSettings>, required: true },
    /** Current map bearing in degrees; drives the mini compass. */
    bearing: { type: Number, default: 0 },
  },
  emits: ['open-settings', 'reset-north'],
  computed: {
    ...mapState({
      focusLngLat: (state: any) => state.map.focusLngLat,
      focusSource: (state: any) => state.map.focusSource,
      focusCoordinates: (state: any) => state.map.focusCoordinates,
      focusElevation: (state: any) => state.map.focusElevation,
      userPosition: (state: any) => state.map.userPosition,
    }),
    visibleSlots(): Array<{ label: string; value: string }> {
      const units = MeasurementPrefUtility.preference();
      const inputs = {
        focus: this.focusLngLat,
        coordinates: this.focusCoordinates,
        elevation: this.focusElevation,
        user: this.userPosition,
        units,
      };
      return this.settings.slots
        .filter((slot) => slot !== 'Empty')
        .map((slot) => ({ label: slotLabel(slot), value: formatSlotValue(slot, inputs) }));
    },
    focusSourceIcon(): string {
      return this.focusSource === 'crosshair' ? 'center_focus_strong' : 'my_location';
    },
    focusSourceTitle(): string {
      return this.focusSource === 'crosshair'
        ? this.$t('map.info_bar.values_at_the_crosshair')
        : this.$t('map.info_bar.values_under_the_mouse');
    },
    compassRotationDeg(): number {
      return compassRotation(this.bearing);
    },
  },
  methods: {
    resetNorth() {
      this.$emit('reset-north');
    },
  },
});
</script>

<style scoped>
.map-info-bar {
  display: flex;
  align-items: stretch;
  gap: 0;
  font-size: .8em;
  line-height: 1;
  background: rgb(27 27 29 / 90%);
  color: #fff;
  width: fit-content;
  max-width: calc(100vw - 32px);
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  overflow: hidden;
  z-index: 100;
}

.map-info-bar__icon {
  display: flex;
  align-items: center;
  padding: 0 6px;
  font-size: 12px;
  color: #bbb;
}

.map-info-bar__slot {
  display: flex;
  align-items: center;
  padding: 5px 8px;
  border-left: 1px solid rgb(255 255 255 / 12%);
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}

.map-info-bar__compass,
.map-info-bar__settings {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  margin: 0;
  border: none;
  border-left: 1px solid rgb(255 255 255 / 12%);
  background: transparent;
  color: #fff;
  cursor: pointer;
}

.map-info-bar__compass svg {
  transition: transform .15s ease-out;
}

.map-info-bar__settings .material-icons {
  font-size: 16px;
}

.map-info-bar__compass:hover,
.map-info-bar__settings:hover,
.map-info-bar__compass:focus-visible,
.map-info-bar__settings:focus-visible {
  background: rgb(255 255 255 / 10%);
  outline: none;
}

@media (max-width: 768px) {
  .map-info-bar {
    font-size: 0.75em;
    border-radius: 4px;
  }
  .map-info-bar__slot {
    padding: 5px 6px;
  }
}
</style>
